import { tap } from 'rxjs/operators';
import { ELEMENT_SORT_FIELD, ELEMENT_FILTER_DESCRIPTION } from './elements.const';
import { finalize } from 'rxjs/operators';
import { CollectionViewer } from '@angular/cdk/collections';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  SortDirection,
  ElementListFilters,
  ElementsGQL,
  ElementFragment,
  ElementSortDefinitionInput,
  ElementSortField,
} from '@/core/graphql/graphql';
import { PaginationMetadata } from '@/shared/components/table/table.model';
import { SearchbarComponent } from '@/shared/components/table/components/searchbar/searchbar.component';
import { FilterComponent } from '@/shared/components/table/components/filter/filter.component';
import { MatSort, Sort } from '@angular/material/sort';
import { PaginatorComponent } from '@/shared/components/table/components/paginator/paginator.component';
import { Observable } from 'rxjs';
import { TableDataSource } from '@/shared/components/table/table.model';
import { CheckboxFilterComponent } from '@/shared/components/table/components/checkbox-filter/checkbox-filter.component';
import { MatCheckboxChange } from '@angular/material/checkbox';

const DEFAULT_PAGINATION_FACTORY = () => ({
  previousCursors: [],
  currentCursor: null,
  nextCursor: null,
  pageSize: 20,
  count: null,
});

export class ElementDataSource extends TableDataSource<ElementFragment> {
  public readonly _loading$: Observable<boolean>;
  protected _paginator: PaginatorComponent;
  protected _sort: MatSort;
  protected _checkboxFilter: CheckboxFilterComponent;
  protected _searchbar: SearchbarComponent;
  private _currentPagination: PaginationMetadata = DEFAULT_PAGINATION_FACTORY();
  private _sortDefinition: ElementSortDefinitionInput = {
    sortField: ElementSortField.CreatedAt,
    sortDirection: SortDirection.Desc,
  };
  private _filters: ElementListFilters = {
    isPublic: true,
  };
  private readonly _elementsSubject = new BehaviorSubject<ElementFragment[]>([]);
  private readonly _loadingSubject = new BehaviorSubject<boolean>(true);
  private readonly _dataChangedSubject = new Subject<void>();
  private readonly _paginationMetaSubject = new BehaviorSubject<PaginationMetadata>(this._currentPagination);

  public constructor (
    private readonly _elementsGql: ElementsGQL,
  ) {
    super();
    this._loading$ = this._loadingSubject.asObservable();
    this.dataChanged$ = this._dataChangedSubject.asObservable();
  }

  public set paginator(component: PaginatorComponent) {
    this._paginator = component;
    this._paginator.fetchedPageCursor$.subscribe(this.loadData.bind(this));
  }

  public set sort(direction: MatSort) {
    this._sort = direction;
    this._sort.sortChange.subscribe(this.onSortChange.bind(this));
  }

  public set filter(component: FilterComponent) {
    throw new Error('Method not implemented');
  }

  public set searchbar(component: SearchbarComponent) {
    this._searchbar = component;
    this._searchbar.appliedSearchTerm$.subscribe((x) => this.onSearchSubmission(x));
  }

  public set checkboxFilter(component: CheckboxFilterComponent) {
    this._checkboxFilter = component;
    this._checkboxFilter.selectedFilter$.subscribe(this.onFilterChange.bind(this));
    this._checkboxFilter.setFilterDescription(ELEMENT_FILTER_DESCRIPTION);
  }

  public connect(_: CollectionViewer): Observable<readonly ElementFragment[]> {
    return this._elementsSubject.asObservable().pipe(
      tap(_ => this._dataChangedSubject.next()),
    );
  }

  public disconnect(_: CollectionViewer): void {
    this._elementsSubject.complete();
    this._loadingSubject.complete();
  }

  public loadData(currentPageCursor: string | null | undefined): void {
    this._loadingSubject.next(true);
    this._elementsGql
      .fetch({
        filters: this._filters,
        meta: {
          pageSize: this._currentPagination.pageSize,
          pageCursor: currentPageCursor,
        },
        sort: [this._sortDefinition],
      }).pipe(
        finalize(() => this._loadingSubject.next(false)),
      ).subscribe((x) => {
        this._elementsSubject.next(x.data.elements.items);
        this._currentPagination.nextCursor = x.data.elements.meta.nextPageCursor;
        this._currentPagination.count = x.data.elements.meta.count;
        this._paginationMetaSubject.next(this._currentPagination);
        this._paginator.getDatasourceMetaData(this._paginationMetaSubject);
      });
  }

  public onSortChange({ active, direction }: Sort): void {
    if (direction) {
      this._sortDefinition = {
        sortField: ELEMENT_SORT_FIELD(active),
        sortDirection: direction === 'asc' ? SortDirection.Asc : SortDirection.Desc,
      };
    } else {
      this._sortDefinition = {
        sortField: ElementSortField.CreatedAt,
        sortDirection: SortDirection.Desc,
      };
    }
    this.resetDatasourcePagination();
  }

  public onFilterChange(selection: MatCheckboxChange): void {
    this._filters.isPublic = !selection.checked;
    this.resetDatasourcePagination();
  }

  public onSearchSubmission(search: string): void {
    if (search.length > 0) {
      this._filters.elementId = search;
    } else {
      this._filters.elementId = undefined;
    }
    this.resetDatasourcePagination();
  }

  private resetDatasourcePagination(): void {
    this._currentPagination = DEFAULT_PAGINATION_FACTORY();
    this._paginationMetaSubject.next(this._currentPagination);
    this._paginator.resetPaginator();
    this.loadData(null);
  }
}
